import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Table, TableBody, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import React, { useCallback, useEffect, useState } from 'react';
import { WorkflowsRow } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTable/WorkflowsRow/WorkflowsRow';
import { clsx } from 'clsx';
import { WorkflowsDragableRow } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTable/WorkflowsRow/WorkflowsDragableRow';

export const WorkflowsTable = ({
  isCustom = false,
  workflows = [],
  readonly = true,
  compact = true,
  onChange = (_rows?: any) => {},
  rowClassName = '',
}: {
  isCustom?: boolean;
  workflows?: any[];
  readonly?: boolean;
  compact?: boolean;
  onChange?: (rows: any) => void;
  rowClassName?: string;
}) => {
  const [rows, setRows] = useState(workflows);

  const moveRow = useCallback((dragIndex: any, hoverIndex: any) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];

      newRows.splice(dragIndex, 1);
      newRows.splice(hoverIndex, 0, prevRows[dragIndex]);

      onChange(newRows);

      return newRows;
    });
  }, []);

  useEffect(() => {
    setRows(
      workflows.map((workflow) => ({
        ...workflow,
        name: workflow.name || 'Escalation',
        description:
          workflow.description || workflow.type === 'default'
            ? 'When AI lacks the knowledge to assist.'
            : '',
      })),
    );
  }, [workflows]);

  const onWorkflowChange = (workflow, index) => {
    const newRows = rows.map((row, idx) => (index === idx ? workflow : row));

    setRows(newRows);
    onChange(newRows);
  };

  const onWorkflowDelete = (index) => {
    const newRows = rows.filter((row, idx) => index !== idx);

    setRows(newRows);
    onChange(newRows);
  };

  const renderRow = useCallback(
    (row, index) => {
      return isCustom ? (
        <WorkflowsDragableRow
          key={`row-${row.name || 'default'}-${row.type}`}
          index={index}
          row={row}
          moveRow={moveRow}
          readonly={readonly}
          isCustom={isCustom}
          compact={compact}
          onWorkflowChange={onWorkflowChange}
          onWorkflowDelete={onWorkflowDelete}
          rowClassName={rowClassName}
        />
      ) : (
        <WorkflowsRow
          key={`row-${row.name || 'default'}-${row.type}`}
          index={index}
          row={row}
          moveRow={moveRow}
          readonly={readonly}
          isCustom={isCustom}
          compact={compact}
          onWorkflowChange={onWorkflowChange}
          onWorkflowDelete={onWorkflowDelete}
          rowClassName={rowClassName}
        />
      );
    },
    [readonly, rows],
  );

  return Boolean(rows.length) ? (
    isCustom ? (
      <DndProvider backend={HTML5Backend}>
        <Table className={clsx('w-full table-auto')}>
          <TableBody>{rows && rows.map((row, index) => renderRow(row, index))}</TableBody>
        </Table>
      </DndProvider>
    ) : (
      <Table className={clsx('w-full')}>
        <TableBody>{rows && rows.map((row, index) => renderRow(row, index))}</TableBody>
      </Table>
    )
  ) : (
    <div className={clsx('px-5', !compact && 'pt-3.5')}>
      <p className={'text-ebony_opacity_30 text-sm font-medium'}>
        No {isCustom ? 'custom' : 'default'} workflows
      </p>
    </div>
  );
};
